import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { StyledButton, StyledSectionContainer } from '../../../../globalStyled';
import {
  fontCondensed,
  fontUi,
  sectionTitle,
  viewMoreLink,
} from '../../../../theme/typography';

export const StyledPage = styled.div`
  width: 100%;
`;

export const StyledColumns = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  gap: 25px;
  width: 100%;

  @media only screen and (max-width: 1199px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const StyledSection = styled(StyledSectionContainer)`
  overflow: hidden;
  min-width: 0;
  flex: 1;
  margin-bottom: 0;

  &:first-of-type {
    max-width: 710px;
  }

  &:last-of-type {
    max-width: 545px;
  }

  @media only screen and (max-width: 1199px) {
    width: 100%;
    max-width: none;
    margin-bottom: 25px;

    &:first-of-type,
    &:last-of-type {
      max-width: none;
    }

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  h3 {
    ${sectionTitle};
    width: 100%;
    font-size: 18px;
    border-bottom: 1px solid ${({ theme }) => theme.borderSubtle};
    padding: 16px 16px 12px;
    display: flex;
    flex-direction: row;
    align-items: center;
  }
`;

export const StyledStatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1px;
  background: ${({ theme }) => theme.borderSubtle};
`;

export const StyledStat = styled.div`
  ${fontUi};
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  min-width: 0;
  background: ${({ theme }) => theme.card};
`;

export const StyledStatIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 8px;
  background: ${({ theme }) => theme.hover};
  color: ${({ theme, $tone }) => {
    if ($tone === 'danger') return theme.danger;
    if ($tone === 'success') return theme.success;
    return theme.textMuted;
  }};

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const StyledStatCopy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
`;

export const StyledStatLabel = styled.span`
  ${fontUi};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.textFaint};
`;

export const StyledStatValue = styled.strong`
  ${fontUi};
  font-size: 20px;
  font-weight: 700;
  line-height: 1.15;
  color: ${({ theme, $tone }) => {
    if ($tone === 'danger') return theme.danger;
    if ($tone === 'success') return theme.success;
    return theme.text;
  }};
`;

export const StyledStatHint = styled.span`
  ${fontUi};
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.textMuted};
`;

export const StyledSquadLink = styled(Link)`
  ${viewMoreLink};
  margin-top: 2px;
`;

export const StyledList = styled.div`
  border-top: 1px solid ${({ theme }) => theme.borderSubtle};
  padding: 4px 8px 10px;
`;

export const StyledListHead = styled.div`
  ${fontUi};
  display: grid;
  grid-template-columns: ${({ $columns }) => $columns};
  gap: 8px;
  align-items: center;
  padding: 8px 8px 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.textFaint};

  > span:not(:first-child) {
    text-align: right;
  }
`;

export const StyledListRow = styled.div`
  ${fontUi};
  display: grid;
  grid-template-columns: ${({ $columns }) => $columns};
  gap: 8px;
  align-items: center;
  padding: 8px;
  border-top: 1px solid ${({ theme }) => theme.borderSubtle};
  font-size: 13px;
  color: ${({ theme }) => theme.text};

  > span:not(:first-child) {
    text-align: right;
  }
`;

export const StyledPlayerName = styled.strong`
  ${fontCondensed};
  display: block;
  min-width: 0;
  font-weight: 600;
  letter-spacing: -0.02em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StyledMeta = styled.span`
  ${fontUi};
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.textMuted};
  white-space: nowrap;
`;

export const StyledWage = styled.span`
  ${fontUi};
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.text};
  white-space: nowrap;
`;

export const StyledPlaytime = styled.span`
  ${fontUi};
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: ${({ theme, $tone }) => {
    if ($tone === 'danger') return theme.danger;
    if ($tone === 'success') return theme.success;
    return theme.text;
  }};
`;

export const StyledStatus = styled.span`
  ${fontUi};
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.textMuted};
  white-space: nowrap;
`;

export const StyledExtendButton = styled(StyledButton)`
  justify-self: end;
  min-width: 0;
  width: auto;
  height: 28px;
  margin: 0;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 600;
  box-shadow: none;
  && {
    ${({ theme }) =>
      theme.isDark
        ? css`
            background: transparent;
            color: ${theme.text};
            border: 1px solid ${theme.border};
            &:hover:not(:disabled) {
              background: ${theme.hover};
            }
          `
        : css`
            background: ${theme.button};
            color: ${theme.buttonText};
            border: 1px solid ${theme.button};
          `}
  }
`;

export const StyledEmpty = styled.p`
  ${fontUi};
  margin: 8px 8px 4px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.textMuted};
`;
